/**
 * 端点类型枚举与解析
 *
 * 每个 provider/model 需要走不同的上游端点格式：
 * - anthropic-native: Claude 原生 SSE（/v1/messages），支持 thinking_delta
 * - gemini-native:    Gemini 原生 generateContent SSE，支持 thought:true parts
 * - openai-compat:    标准 OpenAI Chat Completions 兼容（大多数站点）
 * - openai-responses: OpenAI Responses API（尽力而为，思考 summary 可能为空）
 * - auto:             由 resolveEndpointKind 按模型名自动推断（不能作为最终路由结果）
 */
export type EndpointKind
  = | 'auto'
    | 'anthropic-native'
    | 'gemini-native'
    | 'openai-compat'
    | 'openai-responses'

/**
 * 将 EndpointKind 解析为实际端点（排除 'auto'）。
 *
 * 模型名匹配规则（大小写不敏感，名称可出现在任意位置）：
 * - 包含 claude → anthropic-native
 * - 包含 gemini → gemini-native
 * - 包含 gpt    → openai-responses
 * - 其余                                → openai-compat
 *
 * @param model    模型名，如 'claude-opus-4-8' / 'gemini-3.1-pro-preview' / 'gpt-5.6-sol'
 * @param override 用户显式指定的端点类型；undefined 或 'auto' 时按模型名推断
 */
export function resolveEndpointKind(
  model: string,
  override?: EndpointKind,
): Exclude<EndpointKind, 'auto'> {
  // 显式 override 优先（'auto' 视为未指定，走推断）
  if (override && override !== 'auto')
    return override

  const normalizedModel = model.toLowerCase()

  // 按优先级判断，兼容 provider 前缀或后缀包裹的模型名。
  if (normalizedModel.includes('claude'))
    return 'anthropic-native'

  if (normalizedModel.includes('gemini'))
    return 'gemini-native'

  if (normalizedModel.includes('gpt'))
    return 'openai-responses'

  // 其余（mistral、自定义等）走 OpenAI 兼容通道
  return 'openai-compat'
}
